"use client";

import { IconPlusOutline18, IconTrashOutline18 } from "nucleo-ui-outline-18";
import { CheckBox } from "@/components/ui/CheckBox";
import { Input } from "@/components/ui/input";
import ResponsiveModal from "@/components/custom/ResponsiveModal";
import { Button } from "@/components/ui/button";
import { useExecuteQuery, useOrgTables, orgDatabaseKeys } from "@/hooks/useOrgDatabase";
import { PlanLimitModal } from "@/components/ui/plan-limit-modal";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UpgradePlanModal } from "@/app/(v2)/billing/_components/UpgradePlanModal";
import { PixelLoader } from "@/components/ui/pixel-loader";
import { useState } from "react";
import { toast } from "@/lib/toast";
import {
  COLUMN_TYPES,
  ALLOWED_COLUMN_TYPES,
  escapeIdent,
  RESERVED_TABLE_PREFIXES,
  SAFE_DEFAULT_PATTERN,
} from "@/lib/db/sqlSafety";
import { usePlanLimitGuard } from "@/hooks/usePlanLimitGuard";
import { useSubscription } from "@/hooks/useSubscription";
import { useListTiers } from "@/hooks/useTiers";
import { useOrganizations } from "@/hooks/useOrganizations";
import { useQueryClient } from "@tanstack/react-query";
import { getSelectedOrgId } from "@/lib/authenticatedFetch";

interface ColumnDef {
  name: string;
  type: string;
  primaryKey: boolean;
  notNull: boolean;
  unique: boolean;
  defaultValue: string;
}

const DEFAULT_COLUMNS: ColumnDef[] = [
  { name: "id", type: "SERIAL", primaryKey: true, notNull: true, unique: false, defaultValue: "" },
];

interface CreateTableModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Pre-fill the table name (e.g., from Caddie's proposed schema). */
  initialTableName?: string;
  /** Pre-fill columns (e.g., from Caddie's proposed schema). */
  initialColumns?: ColumnDef[];
  /** Called with the created table name on success. */
  onCreated?: (tableName: string) => void;
}

export type { ColumnDef };

export function CreateTableModal({
  open,
  onOpenChange,
  initialTableName,
  initialColumns,
  onCreated,
}: CreateTableModalProps) {
  const [tableName, setTableName] = useState(initialTableName ?? "");
  const [columns, setColumns] = useState<ColumnDef[]>(initialColumns ?? DEFAULT_COLUMNS);
  const executeQuery = useExecuteQuery();
  const { data: tables } = useOrgTables();
  const queryClient = useQueryClient();
  const orgId = getSelectedOrgId();
  const { isPlanLimitError, showPlanLimitModal, setShowPlanLimitModal, showUpgradeModal, setShowUpgradeModal } =
    usePlanLimitGuard();
  const { selectedOrgId } = useOrganizations();
  const subscriptionQuery = useSubscription(selectedOrgId ?? undefined);
  const { data: tiers } = useListTiers();

  const resetForm = () => {
    setTableName(initialTableName ?? "");
    setColumns(initialColumns ?? DEFAULT_COLUMNS);
  };

  const addColumn = () => {
    setColumns(prev => [
      ...prev,
      { name: "", type: "TEXT", primaryKey: false, notNull: false, unique: false, defaultValue: "" },
    ]);
  };

  const removeColumn = (index: number) => {
    setColumns(prev => prev.filter((_, i) => i !== index));
  };

  const updateColumn = (index: number, field: keyof ColumnDef, value: string | boolean) => {
    setColumns(prev => prev.map((col, i) => (i === index ? { ...col, [field]: value } : col)));
  };

  const generateSQL = (): string => {
    const pkColumns = columns.filter(col => col.primaryKey);
    const isCompositePK = pkColumns.length > 1;

    const colDefs = columns.map(col => {
      let def = `${escapeIdent(col.name)} ${col.type}`;
      if (col.type === "SERIAL") {
        def = `${escapeIdent(col.name)} SERIAL`;
        if (!isCompositePK) def += " PRIMARY KEY";
      } else if (col.primaryKey && !isCompositePK) {
        def += " PRIMARY KEY";
      }
      if (col.notNull && !col.primaryKey) def += " NOT NULL";
      if (col.unique) def += " UNIQUE";
      if (col.defaultValue) {
        const v = col.defaultValue.trim();
        if (SAFE_DEFAULT_PATTERN.test(v)) {
          // Wrap zero-arg function calls in parens (Postgres requires DEFAULT (expr)
          // for non-literal expressions). Plain literals/keywords go through bare.
          if (/\(\)$/.test(v)) {
            def += ` DEFAULT (${v})`;
          } else {
            def += ` DEFAULT ${v}`;
          }
        } else {
          // Anything outside the allowlist is treated as an opaque string literal,
          // so unsafe expressions like `pg_sleep(1)` get quoted instead of executed.
          def += ` DEFAULT '${v.replace(/'/g, "''")}'`;
        }
      }
      return def;
    });

    if (isCompositePK) {
      const pkList = pkColumns.map(col => escapeIdent(col.name)).join(", ");
      colDefs.push(`PRIMARY KEY (${pkList})`);
    }

    return `CREATE TABLE ${escapeIdent(tableName)} (\n  ${colDefs.join(",\n  ")}\n)`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const trimmed = tableName.trim();
    if (!trimmed) {
      toast.error("Table name is required");
      return;
    }
    if (!/^[a-zA-Z][a-zA-Z0-9_]*$/.test(trimmed)) {
      toast.error("Table name must start with a letter and contain only letters, numbers, underscores");
      return;
    }
    const lowered = trimmed.toLowerCase();
    if (RESERVED_TABLE_PREFIXES.some(p => lowered.startsWith(p))) {
      toast.error(`Table names starting with ${RESERVED_TABLE_PREFIXES.map(p => `'${p}'`).join(" or ")} are reserved`);
      return;
    }
    if (columns.length === 0) {
      toast.error("At least one column is required");
      return;
    }
    if (columns.some(c => !c.name.trim())) {
      toast.error("All columns must have a name");
      return;
    }
    const invalidCol = columns.find(c => !/^[a-zA-Z][a-zA-Z0-9_]*$/.test(c.name.trim()));
    if (invalidCol) {
      toast.error("Column names must start with a letter and contain only letters, numbers, underscores");
      return;
    }
    if (columns.some(c => !ALLOWED_COLUMN_TYPES.has(c.type.toUpperCase()))) {
      toast.error("Invalid column type");
      return;
    }
    const badDefault = columns.find(c => c.defaultValue && /[;]/.test(c.defaultValue));
    if (badDefault) {
      toast.error("Default values must not contain semicolons");
      return;
    }

    try {
      await executeQuery.mutateAsync({ sql: generateSQL() });
      if (orgId) {
        queryClient.invalidateQueries({ queryKey: orgDatabaseKeys.tables(orgId) });
      }
      toast.success(`Table "${trimmed}" created`);
      onCreated?.(trimmed);
      onOpenChange(false);
      resetForm();
    } catch (error) {
      if (isPlanLimitError(error)) return;
      toast.error(error instanceof Error ? error.message : "Failed to create table");
    }
  };

  const sql = tableName ? generateSQL() : "";

  return (
    <>
      <ResponsiveModal
        open={open}
        onOpenChange={o => {
          onOpenChange(o);
          if (!o) resetForm();
        }}
        title="Create Table"
        contentClass="max-w-2xl"
      >
        <div className="mb-6">
          <h3 className="text-ds-text-primary text-lg font-semibold">Create Table</h3>
          <p className="body-sm">Define columns for your new table.</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label htmlFor="tableName" className="text-ds-text-secondary mb-1.5 block text-sm font-medium">
              Table Name
            </label>
            <Input
              id="tableName"
              type="text"
              placeholder="users, orders, products..."
              value={tableName}
              onChange={e => setTableName(e.target.value)}
              maxLength={64}
              required
              className="font-mono"
            />
          </div>

          <div>
            <div className="mb-2 flex items-center justify-between">
              <label className="text-ds-text-secondary text-sm font-medium">Columns</label>
              <button
                type="button"
                onClick={addColumn}
                className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400"
              >
                <IconPlusOutline18 className="h-3 w-3" />
                Add Column
              </button>
            </div>

            <div className="space-y-2">
              {columns.map((col, i) => (
                <div
                  key={i}
                  className="border-ds-border-default bg-ds-surface-muted flex items-center gap-2 rounded-lg border p-2"
                >
                  <Input
                    type="text"
                    placeholder="column_name"
                    value={col.name}
                    onChange={e => updateColumn(i, "name", e.target.value)}
                    className="h-8 flex-1 px-2 py-1.5 font-mono text-xs"
                  />
                  <Select value={col.type} onValueChange={v => updateColumn(i, "type", v)}>
                    <SelectTrigger className="border-ds-neutral-300 text-ds-text-primary bg-ds-surface-card h-8 w-auto rounded border text-xs dark:border-zinc-600">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {COLUMN_TYPES.map(t => (
                        <SelectItem key={t} value={t}>
                          {t}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <CheckBox
                    checked={col.primaryKey}
                    onChange={checked => {
                      if (checked) {
                        // Enforce single PK: uncheck all others first
                        setColumns(prev =>
                          prev.map((c, j) => (j === i ? { ...c, primaryKey: true } : { ...c, primaryKey: false })),
                        );
                      } else {
                        updateColumn(i, "primaryKey", false);
                      }
                    }}
                    label="PK"
                    size="sm"
                  />
                  <CheckBox
                    checked={col.notNull}
                    onChange={checked => updateColumn(i, "notNull", checked)}
                    label="NOT NULL"
                    size="sm"
                  />
                  <CheckBox
                    checked={col.unique}
                    onChange={checked => updateColumn(i, "unique", checked)}
                    label="UQ"
                    size="sm"
                  />
                  <button
                    type="button"
                    onClick={() => removeColumn(i)}
                    className="text-ds-text-disabled hover:bg-ds-neutral-200 dark:hover:bg-ds-neutral-700 rounded p-1 hover:text-red-600"
                  >
                    <IconTrashOutline18 className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {sql && (
            <div>
              <label className="text-ds-text-muted mb-1.5 block text-xs font-medium">SQL Preview</label>
              <pre className="bg-ds-neutral-100 text-ds-text-secondary dark:bg-ds-neutral-800 overflow-x-auto rounded-lg p-3 font-mono text-xs">
                {sql}
              </pre>
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <Button
              type="button"
              variant="white"
              onClick={() => {
                onOpenChange(false);
                resetForm();
              }}
              className="flex-1"
            >
              <span className="text-xs">Cancel</span>
            </Button>
            <Button type="submit" disabled={executeQuery.isPending} className="flex-1">
              <span className="flex items-center gap-2 text-xs">
                {executeQuery.isPending ? (
                  <>
                    <PixelLoader size="xs" />
                    Creating...
                  </>
                ) : (
                  <>
                    <IconPlusOutline18 className="h-3 w-3" />
                    Create Table
                  </>
                )}
              </span>
            </Button>
          </div>
        </form>
      </ResponsiveModal>
      {tiers && (
        <PlanLimitModal
          open={showPlanLimitModal}
          onOpenChange={setShowPlanLimitModal}
          reason="database_table_limit"
          currentTier={subscriptionQuery.data?.subscription.tier ?? "free"}
          currentUsage={tables?.length ?? 0}
          currentLimit={subscriptionQuery.data?.limits.maxTables ?? 0}
          tiers={tiers}
          onUpgrade={() => setShowUpgradeModal(true)}
        />
      )}
      {selectedOrgId && (
        <UpgradePlanModal
          open={showUpgradeModal}
          onOpenChange={setShowUpgradeModal}
          currentTier={subscriptionQuery.data?.subscription.tier ?? "free"}
          currentInterval={subscriptionQuery.data?.subscription.interval ?? "month"}
          orgId={selectedOrgId}
          pendingTier={subscriptionQuery.data?.subscription.pendingTier}
          pendingTierAt={subscriptionQuery.data?.subscription.pendingTierAt}
          pendingInterval={subscriptionQuery.data?.subscription.pendingInterval}
        />
      )}
    </>
  );
}
